import { capitalize } from '@mui/material';
import { CloudAccountType, Provider } from '@repo/api-gw-sdk';

import { useDAL } from '@/contexts/useDAL';
import { useEnvironment } from '@/contexts/useEnvironment';
import { useUser } from '@/contexts/useUser';

import { URLS } from './useCreateAwsAccount';

const BASE_URL =
  'https://console.aws.amazon.com/cloudformation/home?#/stacks/create/review?';

const awsOrganizationURL =
  'https://eon-public-b2b628cc-1d96-4fda-8dae-c3b1ad3ea03b.s3.amazonaws.com/aws-organization.yml';

export const openUrlInNewWindow = async ({
  url,
  title,
  width,
  height,
}: {
  url: string;
  title: string;
  width: number;
  height: number;
}) => {
  const { screen } = window;
  const left = screen.width / 2 - width / 2;
  const top = screen.height / 2 - height / 2;

  const newWindow = window.open(
    url,
    title,
    `scrollbars=yes,width=${width},height=${height},top=${top},left=${left},screenX=${left},screenY=${top}`
  );
  newWindow?.focus();

  await new Promise<void>((resolve) => {
    const interval = setInterval(() => {
      if (newWindow?.closed) {
        clearInterval(interval);
        resolve();
      }
    }, 500);
  });
};

export const useValidateOrganization = () => {
  const dal = useDAL();
  const { data } = dal.cloudAccounts.scanning.useList();
  const scanningAccountId = data?.accounts?.find(
    (x) => x.cloudProvider === Provider.Aws
  )?.providerAccountId;
  const { user } = useUser();
  const { serviceAccountId } = useEnvironment();

  const validateOrganization = async (
    orgRoleName: string,
    accountType: CloudAccountType,
    organizationOUID: string
  ) => {
    if (!orgRoleName || !user || !scanningAccountId) {
      return;
    }

    const searchParams = new URLSearchParams();
    searchParams.append('templateURL', awsOrganizationURL);
    searchParams.append('stackName', 'EonOrganization');
    searchParams.append('param_EonAccountId', user.eonAccount.id);
    searchParams.append('param_OrganizationRoleName', orgRoleName);
    searchParams.append('param_OrgId', organizationOUID);
    if (accountType === CloudAccountType.Source) {
      searchParams.append('param_ScanningAccountId', scanningAccountId);
    }
    if (serviceAccountId) {
      searchParams.append('param_ServiceAccountId', serviceAccountId);
    }

    await openUrlInNewWindow({
      url: `${BASE_URL}${searchParams}`,
      title: 'AWSConsole',
      width: window.screen.width * 0.8,
      height: window.screen.height * 0.8,
    });
  };

  return { validateOrganization };
};

export const useValidateAccount = () => {
  const dal = useDAL();
  const { data } = dal.cloudAccounts.scanning.useList();
  const scanningAccountId = data?.accounts?.find(
    (x) => x.cloudProvider === Provider.Aws
  )?.providerAccountId;
  const { user } = useUser();
  const { serviceAccountId } = useEnvironment();

  const validateAccount = async (
    roleName: string,
    accountType: CloudAccountType
  ) => {
    if (!roleName || !scanningAccountId || !user) {
      return;
    }

    const searchParams = new URLSearchParams();
    searchParams.append('templateURL', URLS[accountType]);
    searchParams.append(
      'stackName',
      `EonBackup${capitalize(accountType.toLowerCase())}`
    );
    searchParams.append('param_EonAccountId', user.eonAccount.id);
    searchParams.append('param_RoleName', roleName);
    if (accountType === CloudAccountType.Source) {
      searchParams.append('param_ScanningAccountId', scanningAccountId);
    }
    if (serviceAccountId) {
      searchParams.append('param_ServiceAccountId', serviceAccountId);
    }

    await openUrlInNewWindow({
      url: `${BASE_URL}${searchParams}`,
      title: 'AWSConsole',
      width: window.screen.width * 0.8,
      height: window.screen.height * 0.8,
    });
  };

  return { validateAccount };
};
